import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '../ui'

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui'
import { Popover, PopoverContent, PopoverTrigger } from '../ui'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface CompanyOption {
  id: string
  company: string
  group: string
  groupId: string
}

export interface GroupOption {
  groupId: string
  name: string
  status: 'ACTIVE' | 'DEACTIVATED'
}

interface AssignUserToCompanyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
  companies: CompanyOption[]
  groups: GroupOption[]
  loading?: boolean
  onAssign: (userId: string, companyIds: string[], groupIds: string[]) => Promise<void>
}

export function AssignUserToCompanyDialog({
  open,
  onOpenChange,
  userId,
  companies,
  groups,
  loading = false,
  onAssign,
}: AssignUserToCompanyDialogProps) {
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([])
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Search state for groups and companies
  const [groupSearchValue, setGroupSearchValue] = useState('')
  const [companySearchValue, setCompanySearchValue] = useState('')
  const [groupComboboxOpen, setGroupComboboxOpen] = useState(false)
  const [companyComboboxOpen, setCompanyComboboxOpen] = useState(false)

  // Helper functions for group management
  const getGroupName = (groupId: string) => {
    return groups.find((group) => group.groupId === groupId)?.name || 'Unknown Group'
  }

  const handleGroupSelect = (groupId: string) => {
    if (!selectedGroupIds.includes(groupId)) {
      setSelectedGroupIds((prev) => [...prev, groupId])
    }
  }

  const handleGroupRemove = (groupIdToRemove: string) => {
    setSelectedGroupIds((prev) => prev.filter((id) => id !== groupIdToRemove))

    // Remove all companies that belong to the removed group
    setSelectedCompanyIds((prev) =>
      prev.filter((companyId) => {
        const company = companies.find((c) => c.id === companyId)
        return company && company.groupId !== groupIdToRemove
      }),
    )
  }

  // Helper functions for company management
  const getCompanyName = (companyId: string) => {
    return companies.find((company) => company.id === companyId)?.company || 'Unknown Company'
  }

  const handleCompanySelect = (companyId: string) => {
    if (companyId === 'SELECT_ALL') {
      // Select all available companies from selected groups
      const companiesForSelectedGroups =
        selectedGroupIds.length > 0
          ? companies.filter((company) => selectedGroupIds.includes(company.groupId))
          : []
      const allAvailableCompanyIds = companiesForSelectedGroups.map((company) => company.id)
      setSelectedCompanyIds(allAvailableCompanyIds)
    } else if (!selectedCompanyIds.includes(companyId)) {
      setSelectedCompanyIds((prev) => [...prev, companyId])
    }
  }

  const handleCompanyRemove = (companyIdToRemove: string) => {
    setSelectedCompanyIds((prev) => prev.filter((id) => id !== companyIdToRemove))
  }

  // Filter out groups that don't have any companies
  const groupsWithCompanies = groups.filter((group) =>
    companies.some((company) => company.groupId === group.groupId),
  )

  const availableGroups = groupsWithCompanies.filter(
    (group) => !selectedGroupIds.includes(group.groupId),
  )

  // Filter companies to only show those from selected groups
  const companiesForSelectedGroups =
    selectedGroupIds.length > 0
      ? companies.filter((company) => selectedGroupIds.includes(company.groupId))
      : []

  const availableCompanies = companiesForSelectedGroups.filter(
    (company) => !selectedCompanyIds.includes(company.id),
  )

  const handleAssign = async () => {
    if ((selectedCompanyIds.length === 0 && selectedGroupIds.length === 0) || isSubmitting) return

    setIsSubmitting(true)
    try {
      // Include both selected groups and groups from selected companies
      const allGroupIds = new Set([
        ...selectedGroupIds,
        ...(selectedCompanyIds
          .map((companyId) => {
            const company = companies.find((c) => c.id === companyId)
            return company?.groupId
          })
          .filter(Boolean) as string[]),
      ])

      await onAssign(userId, selectedCompanyIds, Array.from(allGroupIds))
      onOpenChange(false)
      setSelectedGroupIds([])
      setSelectedCompanyIds([])
      setGroupSearchValue('')
      setCompanySearchValue('')
      setGroupComboboxOpen(false)
      setCompanyComboboxOpen(false)
    } catch (_error) {
      // Error handling is done by parent component
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setSelectedGroupIds([])
    setSelectedCompanyIds([])
    setGroupSearchValue('')
    setCompanySearchValue('')
    setGroupComboboxOpen(false)
    setCompanyComboboxOpen(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Assign User to Groups & Companies</DialogTitle>
          <DialogDescription>
            Select groups and companies to assign this user to. The user will gain access to the
            selected groups and companies.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Groups Selection */}
          <div className="space-y-2">
            <Label htmlFor="groups">Assign to group(s)</Label>
            <div className="space-y-2">
              {selectedGroupIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedGroupIds.map((selectedGroupId) => (
                    <div
                      key={selectedGroupId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-main/10 text-main rounded text-sm hover:translate-y-[-2px] transition-all duration-150 ease-in-out cursor-pointer"
                    >
                      {getGroupName(selectedGroupId)}
                      <button
                        type="button"
                        onClick={() => handleGroupRemove(selectedGroupId)}
                        className="text-main"
                        disabled={isSubmitting}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Popover open={groupComboboxOpen} onOpenChange={setGroupComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={groupComboboxOpen}
                    className={cn('justify-between')}
                    disabled={loading || isSubmitting}
                  >
                    Select groups
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search groups..."
                      value={groupSearchValue}
                      onValueChange={setGroupSearchValue}
                    />
                    <CommandList>
                      <CommandEmpty>No groups found.</CommandEmpty>
                      <CommandGroup>
                        {availableGroups
                          .filter((group) =>
                            group.name.toLowerCase().includes(groupSearchValue.toLowerCase()),
                          )
                          .map((group) => (
                            <CommandItem
                              key={group.groupId}
                              value={group.name}
                              onSelect={() => {
                                handleGroupSelect(group.groupId)
                                setGroupComboboxOpen(false)
                              }}
                            >
                              <Check className="mr-2 h-4 w-4 opacity-0" />
                              <div>
                                <div className="font-medium">{group.name}</div>
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Companies Selection */}
          <div className="space-y-2">
            <Label htmlFor="companies">Assign to company(ies)</Label>
            <div className="space-y-2">
              {selectedCompanyIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedCompanyIds.map((selectedCompanyId) => (
                    <div
                      key={selectedCompanyId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-sm hover:translate-y-[-2px] transition-all duration-150 ease-in-out cursor-pointer"
                    >
                      {getCompanyName(selectedCompanyId)}
                      <button
                        type="button"
                        onClick={() => handleCompanyRemove(selectedCompanyId)}
                        className="text-green-600 hover:text-green-800"
                        disabled={isSubmitting}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Popover open={companyComboboxOpen} onOpenChange={setCompanyComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={companyComboboxOpen}
                    className={cn(
                      'justify-between',
                      selectedGroupIds.length === 0 ? 'opacity-50 cursor-not-allowed' : '',
                    )}
                    disabled={loading || isSubmitting || selectedGroupIds.length === 0}
                  >
                    {selectedGroupIds.length === 0 ? 'Select group(s) first' : 'Select companies'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search companies..."
                      value={companySearchValue}
                      onValueChange={setCompanySearchValue}
                    />
                    <CommandList>
                      <CommandEmpty>No companies found.</CommandEmpty>
                      <CommandGroup>
                        {availableCompanies.length > 1 && (
                          <CommandItem
                            value="SELECT_ALL"
                            onSelect={() => {
                              handleCompanySelect('SELECT_ALL')
                              setCompanyComboboxOpen(false)
                            }}
                          >
                            <Check className="mr-2 h-4 w-4 opacity-0" />
                            <div className="font-semibold">Select all</div>
                          </CommandItem>
                        )}
                        {availableCompanies
                          .filter((company) =>
                            company.company
                              .toLowerCase()
                              .includes(companySearchValue.toLowerCase()),
                          )
                          .map((company) => (
                            <CommandItem
                              key={company.id}
                              value={company.company}
                              onSelect={() => {
                                handleCompanySelect(company.id)
                                setCompanyComboboxOpen(false)
                              }}
                            >
                              <Check className="mr-2 h-4 w-4 opacity-0" />
                              <div className="font-medium">{company.company}</div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="main_outline"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="rounded-md"
          >
            Cancel
          </Button>
          <Button
            variant="main_action"
            onClick={handleAssign}
            disabled={
              isSubmitting ||
              (selectedCompanyIds.length === 0 && selectedGroupIds.length === 0) ||
              loading
            }
            className="rounded-md"
          >
            {isSubmitting
              ? 'Assigning...'
              : `Assign to ${selectedGroupIds.length + selectedCompanyIds.length} ${
                  selectedGroupIds.length + selectedCompanyIds.length === 1 ? 'Item' : 'Items'
                }`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
